import axios from 'axios';
import { Configuration, AuthenticationApi, MFAApi } from './lib/index.js';
import { isRefreshBlocked, RefreshBlockedError, singleFlightRefresh, } from './lib/refresh-single-flight.js';
const fromProcess = typeof process !== 'undefined' ? process.env?.['VITE_IDP_BASE_URL'] : undefined;
const fromImportMeta = globalThis.importMetaEnv
    ?.VITE_IDP_BASE_URL;
const defaultBasePath = fromProcess ?? fromImportMeta ?? '';
function sanitizeBase(input) {
    if (!input)
        return input;
    const trimmed = input.trim();
    if (!trimmed)
        return undefined;
    if (trimmed === '/')
        return '';
    return trimmed.replace(/\/$/, '');
}
function getCookie(name) {
    if (typeof document === 'undefined')
        return null;
    const match = document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`));
    if (!match)
        return null;
    const value = match.slice(name.length + 1);
    return value ? decodeURIComponent(value) : null;
}
function isUnsafeMethod(method) {
    if (!method)
        return false;
    const upper = method.toUpperCase();
    return upper === 'POST' || upper === 'PUT' || upper === 'PATCH' || upper === 'DELETE';
}
function hasHeader(headers, name) {
    if (!headers || typeof headers !== 'object')
        return false;
    const getter = headers.get;
    if (typeof getter === 'function') {
        const value = getter.call(headers, name);
        return typeof value === 'string' ? value.trim().length > 0 : value != null;
    }
    const lower = name.toLowerCase();
    const record = headers;
    const value = record[name] ??
        record[lower] ??
        Object.entries(record).find(([key]) => key.toLowerCase() === lower)?.[1];
    return typeof value === 'string' ? value.trim().length > 0 : value != null;
}
/**
 * Create a configured IDP client. The caller supplies callbacks for token
 * management so this library remains free of global state.
 */
export const createIdpClient = ({ basePath = defaultBasePath, tokenDelivery = 'json', getAccessToken, getRefreshToken, getJti, login, logout, getAudience, }) => {
    const sanitized = sanitizeBase(basePath);
    const normalizedBase = sanitized ?? '';
    const axiosBaseUrl = sanitized ? sanitized : undefined;
    const idpAxios = axios.create({ baseURL: axiosBaseUrl, withCredentials: true });
    const cookieOnly = tokenDelivery === 'cookie';
    let isRefreshing = false;
    let pendingRequests = [];
    const processQueue = (token) => {
        pendingRequests.forEach((cb) => cb(token || undefined));
        pendingRequests = [];
    };
    idpAxios.interceptors.request.use((config) => {
        config.headers = config.headers ?? {};
        if (!cookieOnly) {
            const token = getAccessToken();
            if (token && config.headers) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        // Inject audience header for login/refresh/passkey verify endpoints
        const url = (config.url ?? '').toString().toLowerCase();
        const needsAudienceHeader = url.includes('/auth/login') ||
            url.includes('/auth/refresh') ||
            url.includes('/auth/passkeys/authenticate/verify');
        if (config.headers && needsAudienceHeader) {
            const aud = getAudience?.() ?? 'teetime-web';
            config.headers['X-Client-Audience'] = aud;
        }
        if (config.headers &&
            cookieOnly &&
            (url.includes('/auth/login') ||
                url.includes('/auth/refresh') ||
                url.includes('/auth/mfa/verify-login') ||
                url.includes('/auth/passkeys/authenticate/verify'))) {
            config.headers['X-Token-Delivery'] = 'cookie';
        }
        const needsCsrfHeader = isUnsafeMethod(config.method) || url.includes('/auth/passkeys');
        if (config.headers && needsCsrfHeader && !hasHeader(config.headers, 'X-XSRF-Token')) {
            const xsrf = getCookie('XSRF-TOKEN');
            if (xsrf) {
                config.headers['X-XSRF-Token'] = xsrf;
            }
        }
        // Browsers forbid setting User-Agent headers; ensure none slip through
        if (config.headers) {
            const headers = config.headers;
            if (Object.prototype.hasOwnProperty.call(headers, 'user-agent')) {
                delete headers['user-agent'];
            }
            if (Object.prototype.hasOwnProperty.call(headers, 'User-Agent')) {
                delete headers['User-Agent'];
            }
        }
        return config;
    });
    const config = new Configuration({ basePath: normalizedBase });
    if (typeof window !== 'undefined') {
        const headers = (config.baseOptions?.headers ?? {});
        delete headers['User-Agent'];
        delete headers['user-agent'];
        config.baseOptions = { ...config.baseOptions, headers };
    }
    const authApi = new AuthenticationApi(config, normalizedBase, idpAxios);
    const mfaApi = new MFAApi(config, normalizedBase, idpAxios);
    const verifyEmail = (params) => idpAxios.post('/api/auth/verify-email', params);
    const requestPasswordReset = async (email) => {
        await idpAxios.post('/api/auth/forgot-password', { email });
    };
    const resetPassword = async (params) => {
        await idpAxios.post('/api/auth/reset-password', params);
    };
    // Account lifecycle methods (#10496)
    const requestDeletion = async (reason) => {
        await idpAxios.post('/api/auth/account/delete-request', { reason });
    };
    const confirmDeletion = async (code) => {
        await idpAxios.delete('/api/auth/account', { data: { code } });
    };
    const requestRestore = async (email) => {
        await idpAxios.post('/api/auth/account/restore-request', { email });
    };
    const confirmRestore = async (email, code) => {
        await idpAxios.post('/api/auth/account/restore', { email, code });
    };
    const exportAccountData = async (format = 'json') => {
        const response = await idpAxios.get('/api/auth/account/export', {
            params: { format },
        });
        return response.data;
    };
    const listPasskeys = async () => {
        await idpAxios.get('/api/auth/csrf');
        const response = await idpAxios.get('/api/auth/passkeys');
        return response.data;
    };
    const beginPasskeyRegistration = async () => {
        await idpAxios.get('/api/auth/csrf');
        const response = await idpAxios.post('/api/auth/passkeys/register/options', {});
        return response.data;
    };
    const verifyPasskeyRegistration = async (input) => {
        await idpAxios.post('/api/auth/passkeys/register/verify', input);
    };
    const beginPasskeyAuthentication = async (input = {}) => {
        await idpAxios.get('/api/auth/csrf');
        const response = await idpAxios.post('/api/auth/passkeys/authenticate/options', input);
        return response.data;
    };
    const verifyPasskeyAuthentication = async (input) => {
        const response = await idpAxios.post('/api/auth/passkeys/authenticate/verify', input);
        return response.data;
    };
    const renamePasskey = async (id, input) => {
        await idpAxios.patch(`/api/auth/passkeys/${id}`, input);
    };
    const revokePasskey = async (id, input = {}) => {
        await idpAxios.delete(`/api/auth/passkeys/${id}`, { data: input });
    };
    const getTenantSamlConfig = async (tenantId) => {
        const response = await idpAxios.get(`/api/admin/saml/config/${tenantId}`);
        return response.data;
    };
    const createTenantSamlConfig = async (input) => {
        const response = await idpAxios.post('/api/admin/saml/config', input);
        return response.data;
    };
    const updateTenantSamlConfig = async (tenantId, input) => {
        const response = await idpAxios.put(`/api/admin/saml/config/${tenantId}`, input);
        return response.data;
    };
    const refreshTenantSamlConfig = async (tenantId) => {
        const response = await idpAxios.post(`/api/admin/saml/config/${tenantId}/refresh`);
        return response.data;
    };
    const deleteTenantSamlConfig = async (tenantId) => {
        await idpAxios.delete(`/api/admin/saml/config/${tenantId}`);
    };
    idpAxios.interceptors.response.use((response) => response, async (error) => {
        const originalRequest = error.config;
        const url = (originalRequest?.url ?? '').toString().toLowerCase();
        if (url.includes('/auth/login')) {
            return Promise.reject(error);
        }
        if (originalRequest?.url?.includes('auth/refresh')) {
            return Promise.reject(error);
        }
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            if (isRefreshBlocked()) {
                logout();
                return Promise.reject(error);
            }
            const refreshToken = getRefreshToken();
            const jti = getJti();
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    pendingRequests.push((newToken) => {
                        if (!newToken && !cookieOnly)
                            return reject(error);
                        if (newToken) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        }
                        else if (originalRequest.headers?.Authorization) {
                            delete originalRequest.headers.Authorization;
                        }
                        resolve(idpAxios(originalRequest));
                    });
                });
            }
            isRefreshing = true;
            try {
                // Canonical single-flight refresh — idp-client owns URL,
                // headers, CSRF, token-delivery, and guard state. Each caller
                // parses its own cloned Response.
                const refreshPayload = !cookieOnly && refreshToken ? { refreshToken, jti } : undefined;
                const rawRes = await singleFlightRefresh({
                    basePath: normalizedBase,
                    tokenDelivery: cookieOnly ? 'cookie' : 'json',
                    audience: getAudience?.() ?? 'teetime-web',
                    xsrfToken: getCookie('XSRF-TOKEN'),
                    refreshPayload,
                });
                if (!rawRes.ok) {
                    throw new Error(`Refresh failed: ${rawRes.status}`);
                }
                // Interceptor-local parsing — reads its own Response clone
                const refreshData = (await rawRes.json());
                const nextAccessToken = refreshData?.accessToken;
                if (!cookieOnly && nextAccessToken) {
                    login(nextAccessToken, refreshData?.refreshToken ?? '', refreshData?.jti ?? '', true);
                }
                processQueue(nextAccessToken ?? null);
                if (!cookieOnly && nextAccessToken) {
                    originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
                }
                else if (originalRequest.headers?.Authorization) {
                    delete originalRequest.headers.Authorization;
                }
                return idpAxios(originalRequest);
            }
            catch (refreshError) {
                // Blocked-path short-circuit is already logout()-driven above;
                // any other refresh failure (network, 5xx, 401 raised by the
                // coordinator) terminates this session locally.
                if (!(refreshError instanceof RefreshBlockedError)) {
                    logout();
                }
                processQueue(null);
                return Promise.reject(refreshError);
            }
            finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    });
    return {
        authApi,
        mfaApi,
        verifyEmail,
        axios: idpAxios,
        requestPasswordReset,
        resetPassword,
        tokenDelivery,
        // Account lifecycle methods (#10496)
        requestDeletion,
        confirmDeletion,
        requestRestore,
        confirmRestore,
        exportAccountData,
        listPasskeys,
        beginPasskeyRegistration,
        verifyPasskeyRegistration,
        beginPasskeyAuthentication,
        verifyPasskeyAuthentication,
        renamePasskey,
        revokePasskey,
        getTenantSamlConfig,
        createTenantSamlConfig,
        updateTenantSamlConfig,
        refreshTenantSamlConfig,
        deleteTenantSamlConfig,
    };
};
//# sourceMappingURL=client.js.map